import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaSyncAlt } from "react-icons/fa";
import { RiKey2Fill } from "react-icons/ri";
import { FaUsers, FaArrowRight } from "react-icons/fa";
import Loader from "components/Loaders/Loader";
import axios from "axios";
import openNotification from "components/utils/CustomNotification";
import "./HrPullCards.css";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

const beAddress =
  window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

const API_URL_ORGINFO = `${process.env.REACT_APP_API_URL}/api/org-info`;

interface OrgInfo {
  org_uid: string;
  active_user_count: number;
  inactive_user_count: number;
  admins: string[];
}

const HrPullCards: React.FC = () => {
  useCheckSessionHeartBeat();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<OrgInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState<string>("");
  const [portalParams, setPortalParams] = useState<{
    [key: string]: boolean;
  } | null>(null);
  const [formData, setFormData] = useState<{ [key: string]: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [portalOptions, setPortalOptions] = useState<string[]>([]);

  const sessionUid = localStorage.getItem("session_uid") ?? "";
  const userUid = localStorage.getItem("user_uid") ?? "";

  const formatString = (str: string): string => {
    if (typeof str !== "string") {
      return str;
    }

    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      setLoading(true); // Start loading
      try {
        const response = await axios.get(API_URL_ORGINFO, {
          headers: {
            Accept: "application/json",
            "user-uid": userUid,
          },withCredentials: true,
        });
        if (response.status === 200) {
          setOrganizations(response.data.data);
          // openNotification({
          //   type: "success",
          //   message: "Organizations fetched successfully!",
          // }); // Notify on success
        } else {
          throw new Error("Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
        openNotification({
          type: "error",
          message: "Error fetching organizations.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (showModal) {
      const fetchPortals = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`${beAddress}/api/get-org-portals`, {
            headers: {
              "user-uid": userUid,
            },withCredentials: true,
          });

          if (response.status === 200) {
            setPortalOptions(response.data.data || []);
            // openNotification({
            //   type: "success",
            //   message: "Portal options fetched successfully!",
            // }); // Notify on success
          } else {
            throw new Error("Failed to fetch portals.");
          }
        } catch (error) {
          console.error("Error fetching portal options:", error);
          setPortalOptions([]);
          openNotification({
            type: "error",
            message: "Error fetching portal options.",
          });
        } finally {
          setLoading(false);
        }
      };

      fetchPortals();
    }
  }, [showModal]);

  const handlePortalChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const portalName = e.target.value;
    setSelectedPortal(portalName);

    if (!portalName) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${beAddress}/api/get-portal-params`, {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        },withCredentials: true,
        params: { portal_type: portalName },
      });

      console.log("API Response:", response.data);
      setPortalParams(response.data.data || {});
      setFormData(
        Object.keys(response.data.data || {}).reduce((acc, key) => {
          acc[key] = "";
          return acc;
        }, {} as { [key: string]: string })
      );
      // openNotification({
      //   type: "success",
      //   message: "Portal parameters fetched successfully!",
      // }); // Notify on success
    } catch (error) {
      console.error("Error fetching portal parameters:", error);
      setError("Failed to fetch portal parameters.");
      openNotification({
        type: "error",
        message: "Error fetching portal parameters.",
      }); // Notify on error
      setPortalParams(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPortal) {
      openNotification({ type: "error", message: "Please select a portal." }); // Notify if portal is not selected
      return;
    }

    const missingRequiredFields = Object.entries(portalParams || {}).some(
      ([key, isRequired]) => isRequired && !formData[key]
    );

    if (missingRequiredFields) {
      openNotification({
        type: "error",
        message: "Please fill all required fields.",
      }); // Notify if fields are missing
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Modify requestBody to replace client_key with api_key
      const requestBody = { ...formData };
      if (requestBody.client_key) {
        requestBody.api_key = requestBody.client_key;
        delete requestBody.client_key;
      }

      const response = await axios.post(
        `${beAddress}/api/pull-portal-data`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "user-uid": userUid,
            "portal-type": selectedPortal, // Ensure correct casing as per backend
          },withCredentials: true,
        }
      );

      const isSuccess = response.data?.status === "Success";
      const pullUid = response.data?.data?.pull_uid;

      if (isSuccess && pullUid) {
        openNotification({
          type: "success",
          message: response.data?.message || "Portal data pulled successfully.",
        });

        setFormData({});
        setSelectedPortal("");
        setPortalParams({});
        setShowModal(false);

        navigate("/hr-view-history", {
          state: {
            pullUid,
          },
        });
      } else {
        openNotification({
          type: "error",
          message:
            response.data?.data ||
            response.data?.data?.data ||
            "Failed to pull portal data.",
        });
      }

    } catch (error) {
      console.error("Error fetching portal data:", error);
      setError("Failed to fetch portal data.");
      openNotification({
        type: "error",
        message: "Error fetching portal data.",
      }); // Notify on error
    } finally {
      setLoading(false);
    }
  };

  const filteredOrganizations =
    userUid === localStorage.getItem("user_uid") // Directly compare with userUid
      ? organizations // Show all organizations if user is a system admin
      : organizations.filter((org) => org.admins.includes(userUid));

  const resetModalState = () => {
  setSelectedPortal("");
  setPortalParams(null);
  setFormData({});
  setError(null);
  setShowModal(false);
};

const isSubmitDisabled = () => {
  if (!portalParams) return true;

  return Object.entries(portalParams).some(
    ([key, isRequired]) => isRequired && !formData[key]?.trim()
  );
};


  return (
    <div className="hr-pull-container">
      <div className="hr-top-container">
        <h1 className="hr-page-title">HR Pull Data</h1>

        <div className="hr-top-actions">
          <button
            className="hr-pull-history-button"
            onClick={() => navigate("/hr-view-history")}
          >
            <FaArrowRight /> Pull History
          </button>
          <button
            className="hr-pull-sync-button"
            onClick={() => setShowModal(true)}
          >
            <FaSyncAlt /> Pull Now
          </button>
        </div>
      </div>
      {loading ? (
        <Loader />
      ) : (
        <div className="cards-container">
          {filteredOrganizations.length > 0 ? (
            filteredOrganizations.map((org) => {
              const totalUsers =
                org.active_user_count + org.inactive_user_count;

              return (
                <div key={org.org_uid} className="hr-organization-card">
                  <div className="hr-card-header">
                    <FaUsers className="hr-icon" />
                    <div className="hr-organization-details">
                      <h2 className="hr-organization-name">{org.org_uid}</h2>
                      <p className="hr-organization-email">
                        ADMIN: {org.admins.join(", ")}
                      </p>
                    </div>
                  </div>
                  <div className="hr-card-body">
                    <div className="hr-user-stats">
                      <p className="hr-total-users">{totalUsers} Total users</p>
                      <p className="hr-active-users">
                        {org.active_user_count} Active users
                      </p>
                    </div>
                    {/* <button
                      className="hr-view-users-button"
                      onClick={() =>
                        navigate(`/hr-view-history?hr=${org.org_uid}`, {
                          state: {
                            totalUsers,
                            activeUsers: org.active_user_count,
                            inactiveUsers: org.inactive_user_count,
                          },
                        })
                      }
                    >
                      View history <FaArrowRight />
                    </button> */}
                  </div>
                  <div className="hr-card-footer">
                    <p className="last-updated">Last updated Time: N/A</p>
                    <p className="update-info">Last updated by: N/A</p>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No organizations available.</p>
          )}
        </div>
      )}

      {showModal && (
        <div className="hr-modal-overlay">
          <div className="hr-modal-content">
            <div className="hr-modal-heading">
              <h2>
                <RiKey2Fill /> Generate Token
              </h2>
              <button
                className="hr-close-button"
                onClick={resetModalState}
              >
                ✖
              </button>
            </div>

            <div className="hr-modal">
              <div className="hr-modal-field">
                <label>Choose Portal:</label>
                <select
                  className="hr-modal-input"
                  value={selectedPortal}
                  onChange={handlePortalChange}
                >
                  <option value="" disabled>Select Portal</option>
                  {portalOptions.map((portal) => (
                    <option key={portal} value={portal}>
                      {portal}
                    </option>
                  ))}
                </select>
              </div>

              {loading && <p>Loading portal parameters...</p>}
              {error && <p className="error">{error}</p>}

              {portalParams && Object.keys(portalParams).length > 0 &&(
                <form onSubmit={handleSubmit}>
                  {Object.entries(portalParams).map(([key, isRequired]) => (
                    <div className="hr-modal-field" key={key}>
                      <label htmlFor={key}>{formatString(key)}:</label>
                      <input
                        id={key}
                        name={key}
                        type="text"
                        className="hr-modal-input"
                        required={isRequired}
                        placeholder={`Enter ${formatString(key)}`}
                        value={formData[key]}
                        onChange={handleInputChange}
                      />
                    </div>
                  ))}

                  <div className="hr-modal-buttons">
                    <button type="submit" className="hr-submit-button" disabled={isSubmitDisabled()}>
                      Submit
                    </button>
                    <button
                      className="hr-cancel-button"
                      onClick={resetModalState}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HrPullCards;
